/**
 * ImageCropModal
 *
 * A modal that lets the coach zoom, pan, and rotate their uploaded photo
 * so the face ends up nicely centred in the circular avatar frame.
 *
 * Uses react-easy-crop for the cropper UI and returns a new File from
 * cropImage.js when the user clicks "Save".
 */
import React, { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import getCroppedImgFile from './cropImage';
import { PopupShell } from '../../components';
import popupCss from '../../components/PopupShell/PopupShell.css';
import css from './ImageCropModal.css';

const ImageCropModal = props => {
  const {
    isOpen,
    imageSrc,
    fileName,
    onCancel,
    onConfirm,
    cropShape = 'round',
    aspect = 1,
    title = 'Adjust your profile photo',
    subtitle = 'Zoom and drag to centre your face',
    saveLabel = 'Save photo',
  } = props;

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const onCropComplete = useCallback((croppedArea, croppedAreaPx) => {
    setCroppedAreaPixels(croppedAreaPx);
  }, []);

  // This component instance is never unmounted between opens — Gallery.js
  // (and ProfileSettingsForm) always render <ImageCropModal isOpen={...} />
  // and rely on the `if (!isOpen || !imageSrc) return null` below to hide
  // it, rather than removing it from the tree. That means all state here
  // (crop/zoom/rotation/isProcessing) persists across images unless reset
  // explicitly. Reset on every new `imageSrc` so cropping a second (or
  // third) image starts from a clean slate instead of carrying over the
  // previous image's zoom/rotation — and, critically, isProcessing: with
  // no reset, a coach adding a 2nd+ photo would reopen the modal with
  // isProcessing still stuck from the previous save, showing "Saving..."
  // with the button disabled and no way to actually save it.
  useEffect(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    setIsProcessing(false);
  }, [imageSrc]);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    setIsProcessing(true);
    try {
      const croppedFile = await getCroppedImgFile(
        imageSrc,
        croppedAreaPixels,
        rotation,
        fileName || 'profile.jpg'
      );
      onConfirm(croppedFile);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to crop image', err);
    } finally {
      // Always clear, success or failure — the old code only cleared it
      // in the catch block, so a successful save left isProcessing stuck
      // `true` forever (see the effect above for the full explanation).
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  };

  if (!isOpen || !imageSrc) return null;

  return (
    <PopupShell
      onClose={onCancel}
      ariaLabel={title}
      title={title}
      subtitle={subtitle}
      maxWidth={560}
      footer={
        <div className={popupCss.popupShellNavRow}>
          <button
            type="button"
            className={`${popupCss.popupShellBtn} ${popupCss.popupShellBtnSecondary}`}
            onClick={onCancel}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`${popupCss.popupShellBtn} ${popupCss.popupShellBtnPrimary}`}
            onClick={handleSave}
            disabled={isProcessing || !croppedAreaPixels}
          >
            {isProcessing ? 'Saving...' : saveLabel}
          </button>
        </div>
      }
    >
      <div className={css.cropperWrapper}>
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={aspect}
          cropShape={cropShape}
          showGrid={false}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onRotationChange={setRotation}
          onCropComplete={onCropComplete}
          restrictPosition={false}
        />
      </div>

      <div className={css.controls}>
        <div className={css.sliderRow}>
          <label htmlFor="zoom-slider" className={css.sliderLabel}>
            Zoom
          </label>
          <input
            id="zoom-slider"
            type="range"
            min={1}
            max={4}
            step={0.01}
            value={zoom}
            onChange={e => setZoom(Number(e.target.value))}
            className={css.slider}
            aria-label="Zoom"
          />
        </div>

        <div className={css.sliderRow}>
          <label htmlFor="rotation-slider" className={css.sliderLabel}>
            Rotate
          </label>
          <input
            id="rotation-slider"
            type="range"
            min={0}
            max={360}
            step={1}
            value={rotation}
            onChange={e => setRotation(Number(e.target.value))}
            className={css.slider}
            aria-label="Rotate"
          />
        </div>

        <button type="button" className={css.resetButton} onClick={handleReset}>
          Reset
        </button>
      </div>
    </PopupShell>
  );
};

export default ImageCropModal;
